import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Cotizador.module.scss';
import { submitLandingForm } from '../../api/LandingApi';
import cotizadorData from '../../data/cotizadorData.json';
import TagManager from 'react-gtm-module';


const Cotizador = ({ onClose }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [formData, setFormData] = useState({
    pais: '',
    sectors: [],
    machines: '',
    name: '',
    city: '',
    email: '',
    phone: '',
    company: '',
    ruc: ''
  });

  const totalSteps = cotizadorData.steps.length;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      TagManager.dataLayer({
        dataLayer: {
          event: 'ux_event',
          event_name: 'cotizador_abierto',
          event_category: 'engagement',
          event_label: 'Cotizador abierto',
          value: 1
        }
      });
    }
  }, [formData.pais]);

  const handleSelectCountry = (opt) => {
    setFormData(prev => ({ ...prev, pais: opt.value }));

    if (opt.route) {
      onClose();
      navigate(opt.route);
      return;
    }

    setCurrentStep(1);
  };

  const handleCheckboxChange = (value) => {
    const list = formData.sectors.includes(value)
      ? formData.sectors.filter(s => s !== value)
      : [...formData.sectors, value];
    setFormData(prev => ({ ...prev, sectors: list }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep = () => {
    if (currentStep === 0) return true;
    const step = cotizadorData.steps[currentStep - 1];
    const val = formData[step.name];
    if (step.required) {
      if (!val || (Array.isArray(val) && val.length === 0)) {
        alert(`${step.title} es obligatorio.`);
        return false;
      }
    }
    if (step.inputType === 'email' && val) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(val.trim())) {
        alert('Ingresa un correo electrónico válido.');
        return false;
      }
    }
    if (step.inputType === 'tel' && val) {
      if (!/^\d+$/.test(val.trim())) {
        alert('El teléfono debe contener solo números.');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (!validateStep()) return;
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => setCurrentStep(prev => prev - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitLandingForm(formData);
      TagManager.dataLayer({
        dataLayer: {
          event: 'ux_event',
          event_name: 'lead_enviado',
          event_category: 'engagement',
          event_label: 'Solicitud de Cotización',
          value: 1
        }
      });
      setSubmissionStatus('success');
    } catch {
      setSubmissionStatus('error');
    }
  };

  const renderStep = () => {
    if (currentStep === 0) {
      return (
        <div className={styles.formGroup}>
          {cotizadorData.countryOptions.map((opt, i) => (
            <button
              key={i}
              type="button"
              className={styles.navButton}
              style={{ display: 'block', width: '100%', marginBottom: '12px' }}
              onClick={() => handleSelectCountry(opt)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );
    }

    const step = cotizadorData.steps[currentStep - 1];
    switch (step.type) {
      case 'checkbox':
        return (
          <div className={styles.formGroup}>
            <label className={styles.questionLabel}>{step.title}</label>
            <div className={styles.checkboxGroup}>
              {step.options.map((opt, i) => (
                <div key={i} className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    id={`${step.name}-${i}`}
                    checked={formData.sectors.includes(opt)}
                    onChange={() => handleCheckboxChange(opt)}
                  />
                  <label htmlFor={`${step.name}-${i}`}>{opt}</label>
                </div>
              ))}
            </div>
          </div>
        );
      case 'input':
        return (
          <div className={styles.formGroup}>
            <label className={styles.questionLabel}>{step.title}</label>
            <input
              type={step.inputType}
              name={step.name}
              value={formData[step.name]}
              onChange={handleChange}
              placeholder={step.placeholder}
              required={step.required}
            />
          </div>
        );
      case 'subgroup':
        return (
          <div className={styles.formGroup}>
            <label className={styles.questionLabel}>{step.title}</label>
            <div className={styles.subGroup}>
              {step.fields.map((f, i) => (
                <input
                  key={i}
                  type="text"
                  name={f.name}
                  value={formData[f.name]}
                  onChange={handleChange}
                  placeholder={f.placeholder}
                />
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (submissionStatus) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <button className={styles.closeButton} onClick={onClose}>X</button>
          <h2 className={styles.resultTitle}>
            {submissionStatus === 'success' ? '¡Éxito!' : 'Error'}
          </h2>
          <p className={styles.resultMessage}>
            {submissionStatus === 'success'
              ? '¡Mensaje enviado exitosamente! Nos estaremos poniendo en contacto a la brevedad.'
              : 'Hubo un error al enviar tu información. Por favor, inténtalo nuevamente.'}
          </p>
          <button className={styles.resultButton} onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>X</button>
        <h2 className={styles.modalTitle}>
          {currentStep === 0 ? 'Elige tu país' : 'Cotizador'}
        </h2>
        <form
          onSubmit={currentStep === totalSteps ? handleSubmit : e => { e.preventDefault(); nextStep(); }}
        >
          {renderStep()}
          {currentStep > 0 && (
            <div className={styles.buttonGroup}>
              {currentStep > 1 && (
                <button type="button" className={styles.navButton} onClick={prevStep}>
                  Anterior
                </button>
              )}
              {currentStep < totalSteps ? (
                <button type="button" className={styles.navButton} onClick={nextStep}>
                  Siguiente
                </button>
              ) : (
                <button type="submit" className={styles.navButton}>
                  Enviar
                </button>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Cotizador;